import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useStaticTranslation } from "@/lib/staticTranslations";

const About = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useStaticTranslation("about");
  const [animatedValues, setAnimatedValues] = useState({
    year: 0,
    experience: 0,
    satisfaction: 0
  });

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const fps = 60;
    const totalFrames = (duration / 1000) * fps;
    
    const targets = {
      year: 1984,
      experience: 40,
      satisfaction: 100
    };

    let frame = 0;
    const interval = setInterval(() => {
      frame++;
      const progress = Math.min(frame / totalFrames, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedValues({
        year: Math.round(targets.year * easeProgress),
        experience: Math.round(targets.experience * easeProgress),
        satisfaction: Math.round(targets.satisfaction * easeProgress)
      });

      if (progress >= 1) {
        clearInterval(interval);
      }
    }, 1000 / fps);

    return () => clearInterval(interval);
  }, [isVisible]);
  
  return (
    <section 
      id="about" 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-20 px-4 bg-muted/30"
    >
      <div className="container mx-auto max-w-6xl">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7 }}
        >
          <div className="gold-divider mb-6 mx-auto" />
          <h2 className="text-3xl md:text-4xl font-serif mb-6">{t("aboutAlice", "אודות אליס")}</h2>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div 
            className="space-y-4 md:space-y-6 rtl:text-right ltr:text-left"
            initial={{ opacity: 0, x: 50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <p className="text-base md:text-lg text-foreground leading-relaxed">
              <strong>אליס תכשיטים ושעונים</strong>, נוסדה בשנת 1984 ע"י הבעלים דוד בורמד ואחיו אלברט בורמד. 
              החנות אליס, נקראת על שם רעיותו של דוד, אליס בורמד.
            </p>
            
            <motion.div
              initial={false}
              animate={{ 
                height: isExpanded ? "auto" : 0,
                opacity: isExpanded ? 1 : 0
              }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
            >
              <div className="space-y-4 md:space-y-6">
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  אליס החלה את דרכה במכירת שעונים ותכשיטי זהב ועם השנים התקדמה והתפתחה והפכה להיות 
                  רשת התכשיטים המובילה בדרום, אשר ניתן למצוא בה את כל סוגי התכשיטים, טבעות אירוסין, 
                  טבעות יהלומים, טבעות נישואין לנשים וגברים ועוד מגוון רחב של סגנונות תכשיטים לנשים וגברים.
                </p>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  בנוסף, ניתן למצוא באתר ובחנויות מבחר רחב של מותגי שעונים המובילים בתחום. 
                  כמו כן אליס עובדת עם מיטב היצרנים המובילים בתחום הזהב והיהלומים.
                </p>
                <p className="text-base md:text-lg text-primary/90 font-medium leading-relaxed">
                  אליס מדורגת גבוה בכל הביקורות אונליין, בזכות היחס החם ובשירות האדיב שניתן ללקוחותיה 
                  מזה עשרות שנים.
                </p>
              </div>
            </motion.div>

            <div className="flex flex-wrap gap-3 mt-2">
              <Button
                variant="outline"
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 border-none shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {isExpanded ? t("showLess", "הצג פחות") : t("showMore", "הצג עוד")}
                <ChevronDown 
                  className={`h-4 w-4 mr-2 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                />
              </Button>
              
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Button
                    asChild
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                  >
                    <Link to="/about">
                      {t("fullAboutPage", "לעמוד אודות המלא")}
                      <ArrowLeft className="h-4 w-4 mr-2" />
                    </Link>
                  </Button>
                </motion.div>
              )}
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-4 md:gap-6">
            {[
              { key: "year", label: t("yearFounded", "שנת ייסוד"), delay: 0.3, suffix: "" },
              { key: "experience", label: t("yearsExperience", "שנות ניסיון"), delay: 0.4, suffix: "+" },
              { key: null, label: t("leadingSouth", "בדרום הארץ"), delay: 0.5, text: t("leadingSouth", "מובילה") },
              { key: "satisfaction", label: t("satisfaction", "שביעות רצון"), delay: 0.6, suffix: "%" }
            ].map((stat) => (
              <motion.div
                key={stat.label}
                className="bg-background p-4 md:p-8 rounded-sm elegant-border text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5, delay: stat.delay }}
              >
                <h3 className="text-2xl md:text-4xl font-bold text-primary mb-1 md:mb-2">
                  {stat.key !== null ? (
                    <span>{animatedValues[stat.key as keyof typeof animatedValues]}{stat.suffix}</span>
                  ) : (
                    <span>{stat.text}</span>
                  )}
                </h3>
                <p className="text-xs md:text-base text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
